import { useLayoutEffect, useMemo, type ReactNode } from "react";
import { AuthKitProvider, useAuth as useAuthKit } from "@workos-inc/authkit-react";
import { E2E, env, SAMPLE_DATA } from "@/config/env";
import { setAccessTokenProvider, setUnauthorizedHandler } from "@/lib/api/client";
import { autoSignIn } from "@/lib/signInGuard";
import { resetAnalytics } from "@/lib/analytics";
import { tellExtensionSignedOut, useExtensionPresence } from "./extension";
import { SAMPLE_SESSION, sampleSignedOut } from "./sample";
import { E2E_SIGN_IN_PATH, E2E_SIGN_UP_PATH, E2E_TOKEN, e2eSession, e2eSignedOut } from "./e2e";
import { AuthContext, type Auth, type Session } from "./session";

/**
 * The sign-in, at the root of every page: WorkOS AuthKit. It never sends
 * anyone to sign in by itself. The signed-in app asks for that when there
 * is no user; the signed-out pages read where it stands and offer the
 * sign-in. From the moment there is a user, every API request carries
 * their access token. The token never leaves this page's script: the
 * extension signs in on its own, and is only told when the person signs
 * out here (./extension).
 *
 * The sample-data build has no API to sign in to, and renders for a
 * placeholder person, or signed out with `?sample=signedout`.
 */
export default function AuthProvider({ children }: { children: ReactNode }) {
    // Whether the extension is installed, for the install hint, in every build.
    useExtensionPresence();
    if (SAMPLE_DATA) {
        return <SampleAuth>{children}</SampleAuth>;
    }
    if (E2E) {
        return <E2eAuth>{children}</E2eAuth>;
    }
    // devMode in every build: AuthKit then keeps the refresh token in
    // localStorage. Without a custom WorkOS auth domain the session cookie
    // sits on api.workos.com, which this page cannot see, so a token held
    // only in memory left every reload signed out.
    return (
        <AuthKitProvider
            clientId={env.WORKOS_CLIENT_ID}
            apiHostname={env.WORKOS_API_HOSTNAME}
            devMode
        >
            <WorkOsAuth>{children}</WorkOsAuth>
        </AuthKitProvider>
    );
}

const SAMPLE_SIGNED_OUT = typeof window !== "undefined" && sampleSignedOut(window.location.search);

function SampleAuth({ children }: { children: ReactNode }) {
    const auth = useMemo<Auth>(() => {
        // Signing in from the signed-out sample reloads it signed in.
        const enter = () => window.location.assign("/");
        return {
            state: SAMPLE_SIGNED_OUT
                ? { status: "signed-out" }
                : { status: "signed-in", session: SAMPLE_SESSION },
            signIn: enter,
            signUp: enter,
        };
    }, []);
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

/** The synthetics' sign-in: see ./e2e. */
function E2eAuth({ children }: { children: ReactNode }) {
    const auth = useMemo<Auth>(() => {
        const leave = (path: string) => window.location.assign(path);
        const signedOut = e2eSignedOut(window.localStorage);
        return {
            state: signedOut
                ? { status: "signed-out" }
                : { status: "signed-in", session: e2eSession(leave) },
            signIn: () => leave(E2E_SIGN_IN_PATH),
            signUp: () => leave(E2E_SIGN_UP_PATH),
        };
    }, []);
    // A layout effect, as WorkOsAuth's: see there.
    useLayoutEffect(() => {
        if (auth.state.status !== "signed-in") return;
        setAccessTokenProvider(async () => E2E_TOKEN);
        // As for a WorkOS session: a 401 sends the person to sign in, once in a while.
        setUnauthorizedHandler(() => autoSignIn(auth.signIn));
        return () => {
            setAccessTokenProvider(null);
            setUnauthorizedHandler(null);
        };
    }, [auth]);
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

function WorkOsAuth({ children }: { children: ReactNode }) {
    const { isLoading, user, signIn, signUp, signOut, getAccessToken } = useAuthKit();

    // The token getter goes to the client the moment there is a user, and
    // comes back out when there is not, so a signed-out page never sends a
    // stale one: its requests go out with no header at all.
    //
    // A layout effect, not a passive one: the pages under this render in
    // the same commit as the user arrives, and their queries start in
    // their own passive effects, which run before a parent's. With a
    // passive effect here the first requests of a sign-in went out with
    // no token, came back 401, and could send a person who had just
    // signed in back to the sign-in. Layout effects all run first.
    useLayoutEffect(() => {
        if (!user) {
            setAccessTokenProvider(null);
            setUnauthorizedHandler(null);
            return;
        }
        setAccessTokenProvider(() => getAccessToken());
        // The API says the session is gone: sign in again, once in a while.
        setUnauthorizedHandler(() => autoSignIn(() => void signIn()));
        return () => {
            setAccessTokenProvider(null);
            setUnauthorizedHandler(null);
        };
    }, [user, getAccessToken, signIn]);

    // Analytics knows the person by their WorkOS id alone, and only once
    // their record says they agreed: see components/consent/ConsentGate.

    const session = useMemo<Session | null>(
        () =>
            user
                ? {
                      userId: user.id,
                      email: user.email ?? null,
                      firstName: user.firstName ?? null,
                      lastName: user.lastName ?? null,
                      createdAt: user.createdAt ?? null,
                      signOut: () => {
                          resetAnalytics();
                          // The extension signs out before AuthKit takes
                          // the page away: it says when it has, or the
                          // wait runs out.
                          void tellExtensionSignedOut(window).then(() =>
                              signOut({ returnTo: env.SITE_URL }),
                          );
                      },
                  }
                : null,
        [user, signOut],
    );

    const auth = useMemo<Auth>(
        () => ({
            state: isLoading
                ? { status: "loading" }
                : session
                  ? { status: "signed-in", session }
                  : { status: "signed-out" },
            signIn: () => void signIn(),
            signUp: (email) => void signUp(email ? { loginHint: email } : undefined),
        }),
        [isLoading, session, signIn, signUp],
    );

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
